import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";

import { useUser } from "@/web/auth/useUser";
import { Redirect } from "@/web/components/Redirect";
import { Button, buttonVariants } from "@/web/components/primitives/button";
import { Card, CardContent } from "@/web/components/primitives/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/web/components/primitives/dialog";
import { toaster } from "@/web/components/primitives/toast";
import { DefaultNavbar } from "@/web/components/styled/AppNavbar";
import { ErrorHandler } from "@/web/lib/errors";
import { useFeatureFlag } from "@/web/lib/featureFlags";
import { tableQueryOptions, useProcedureMutation } from "@/web/lib/tanstack";
import { procedures, tables } from "@tycho/web-bindings";
import type {
  InvestorInvestmentRow,
  InvestorNominatedBusinessRow,
  InvestorPledgedCardRow,
  InvestorPledgedNominationRow,
} from "@tycho/web-bindings/types";
import {
  InvestmentCard,
  NominationCard,
  NominationReservationCard,
  ReservationCard,
} from "@/web/routes/_protected/profile/-inv/PortfolioCards";
import { statusKey } from "@/web/routes/_protected/profile/-inv/orderStatus";
import {
  PortfolioTabs,
  type PortfolioTab,
} from "@/web/routes/_protected/profile/-inv/PortfolioTabs";
import {
  type AboutYouData,
  aboutYouDataFromUserInfo,
  AboutYouForm,
  buildPartialAboutYouUserInfoMutationArgs,
  isAboutYouDataValid,
  useAboutYouForm,
} from "@/web/routes/_protected/smb/-onboarding/AboutYouForm";

export const Route = createFileRoute("/_protected/profile/inv")({
  component: InvDashboard,
});

function InvDashboard() {
  const user = useUser();
  if (!user) {
    return null;
  }
  if (user.investorIds.length === 0) {
    return <Redirect to="/onboarding/inv" replace />;
  }
  return <InvDashboardContent />;
}

function InvDashboardContent() {
  const user = useUser();

  const { data: dashboardRows } = useSuspenseQuery(tableQueryOptions(tables.investorDashboard));
  const dashboard = dashboardRows[0];
  const pledgedCards = dashboard?.pledgedCards ?? [];
  // Pledges on NOMINATED businesses — the other half of "reserved". Stored in a
  // different table from card pledges (see the view), so it arrives as its own
  // list and is merged into the Reserved tab below.
  const pledgedNominations = dashboard?.pledgedNominations ?? [];
  const transactAccount = dashboard?.transactAccount;

  const { data: userInfoRows } = useSuspenseQuery(tableQueryOptions(tables.currentUserInfo));
  const existingUserInfo = userInfoRows[0];
  const prefill = existingUserInfo
    ? aboutYouDataFromUserInfo(existingUserInfo, user?.email ?? "")
    : undefined;

  // Transact account creation is feature-flagged (server enforces the same flag
  // as a kill switch — see createTrio). When off, we hide the CTA entirely;
  // onboarding + the pledged-cards view are NOT gated. An already-created account
  // still shows its "set up" state so we don't hide history if the flag flips off.
  const transactEnabled = useFeatureFlag("TRANSACT_INVESTOR_ACCOUNTS");

  const hasTransactAccount = transactAccount !== undefined;
  // Enable the CTA only when the required personal-info subset is present. Same
  // validation the AboutYou form enforces; without it the create would fail
  // server-side naming the missing field.
  const infoComplete = prefill !== undefined && isAboutYouDataValid(prefill);

  const [dialogOpen, setDialogOpen] = useState(false);

  return (
    <div className="flex min-h-dvh flex-col">
      <DefaultNavbar />
      {/* Same shell as `/browse`: full-bleed `px-4 md:px-8`, no `max-w`. Both
          pages are filterable grids of the SAME `UnifiedBrowseCard`, so a
          narrower container here would squeeze cards built for the browse
          grid's column counts. */}
      <div className="flex flex-1 flex-col px-4 py-6 md:px-8">
        {/* No wrapper and no reserved height: `InvestorAccountCta` renders
            nothing whenever the account already exists (or the flag is off),
            and a container that outlives its only child is 100px of blank page
            between the navbar and the portfolio. It owns its own spacing when
            it does render. */}
        <InvestorAccountCta
          enabled={transactEnabled}
          hasTransactAccount={hasTransactAccount}
          infoComplete={infoComplete}
          onOpen={() => setDialogOpen(true)}
        />

        <PortfolioSection
          investments={dashboard?.investments ?? []}
          pledgedCards={pledgedCards}
          pledgedNominations={pledgedNominations}
          nominatedBusinesses={dashboard?.nominatedBusinesses ?? []}
        />
      </div>

      <CreateInvestorAccountDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        defaultEmail={user?.email}
        prefill={prefill}
        avatarCdnPath={existingUserInfo?.avatarCdnPath}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// "Create my investor account" CTA
// ---------------------------------------------------------------------------

/**
 * "Set up your investor account" — an admonition, shown ONLY when there is
 * something to do.
 *
 * Both visibility gates live here rather than in a wrapper at the call site:
 * the feature flag and the existing-account check answer the same question
 * ("is there a prompt to show?"), and splitting them across a parent guard and
 * a child early-return is how the page ended up reserving 100px for a component
 * that had already returned `null`. `infoComplete` is NOT a visibility gate — it
 * chooses the copy and disables the button, because a user who cannot act yet
 * still needs to know this step exists.
 *
 * A bordered admonition rather than a bare button because it is now the only
 * thing above the portfolio: a lone button floating over the tabs reads as page
 * chrome, while a bounded block reads as a notice about account state. It
 * carries its own bottom margin so nothing outside it needs to know whether it
 * rendered.
 */
function InvestorAccountCta({
  enabled,
  hasTransactAccount,
  infoComplete,
  onOpen,
}: {
  /** `TRANSACT_INVESTOR_ACCOUNTS`. Server enforces the same flag — see createTrio. */
  enabled: boolean;
  hasTransactAccount: boolean;
  infoComplete: boolean;
  onOpen: () => void;
}) {
  // An existing account needs nothing from the user, so it says nothing. The
  // CTA exists to prompt an action; once that action is done the prompt is just
  // a distraction on a page whose subject is the portfolio below it.
  //
  // The flag is checked here too, so "account exists" still wins if it flips
  // off — an already-created account must never re-prompt.
  if (hasTransactAccount || !enabled) return null;

  return (
    <div className="mb-6 flex flex-col items-start justify-between gap-3 border border-withus-dark-blue/15 p-4 sm:flex-row sm:items-center">
      <div className="flex flex-col gap-1">
        <p className="text-sm font-medium text-withus-dark-blue">Set up your investor account</p>
        {/* The blocked case explains itself in the body rather than in a tooltip
            on the disabled button: the reason a control is unavailable should be
            readable without hovering it, and a tooltip saying the same sentence
            would state one fact twice. */}
        <p className="text-xs text-withus-dark-blue/70">
          {infoComplete
            ? "You'll need this before you can invest in a raise."
            : "Complete your personal info first, then create your investor account."}
        </p>
      </div>
      <Button disabled={!infoComplete} onClick={onOpen} className="shrink-0">
        Create my investor account
      </Button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// AboutYou dialog → upsert info → create Transact trio
// ---------------------------------------------------------------------------

function CreateInvestorAccountDialog({
  open,
  onOpenChange,
  defaultEmail,
  prefill,
  avatarCdnPath,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultEmail?: string;
  prefill?: AboutYouData;
  avatarCdnPath?: string;
}) {
  const { aboutYouForm, aboutYouData } = useAboutYouForm({ defaultEmail, prefill });

  const { mutateAsync: upsertUserInfo, isPending: isSaving } = useProcedureMutation(
    procedures.upsertUserInfo,
  );
  const { mutateAsync: createInvestorTransactAccount, isPending: isCreating } =
    useProcedureMutation(procedures.createInvestorTransactAccount);

  const isBusy = isSaving || isCreating;

  async function handleConfirm() {
    const isValid = await aboutYouForm.trigger();
    if (!isValid) return;
    try {
      // Persist any edits first so the server-side create reads the latest info.
      // Preserve the stored avatar (empty/undefined would clear it — see
      // `upsertUserInfo`'s empty-string-clears semantics).
      await upsertUserInfo({
        ...buildPartialAboutYouUserInfoMutationArgs(aboutYouData, true),
        avatarCdnPath,
      });
      await createInvestorTransactAccount();
      toaster({ message: "Investor account created", variant: "success" });
      onOpenChange(false);
    } catch (error) {
      ErrorHandler.default(error);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90dvh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Create your investor account</DialogTitle>
          <DialogDescription>
            Confirm your details. We use these to set up your regulated investor account.
          </DialogDescription>
        </DialogHeader>

        <AboutYouForm form={aboutYouForm} showAccreditation />

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={isBusy}>
            Cancel
          </Button>
          <Button onClick={() => void handleConfirm()} disabled={isBusy}>
            {isCreating ? "Creating…" : isSaving ? "Saving…" : "Create account"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Active investments
// ---------------------------------------------------------------------------

/**
 * How each order status reads to an INVESTOR. The stored value is our order
 * lifecycle (docs/TRANSACT_CHECKOUT_SPEC.md §3); this is the only place it is
 * translated into user-facing language.
 *
 * Two deliberate choices:
 *   • Nothing here says "failed". A `TRANSACTION_ERROR` order may still have
 *     money in flight — the operator reconciles it — so the copy points the
 *     user at us rather than implying their money is gone.
 *   • `CREATED` is described as "starting", not "pending". It means the fund
 *     move has not been submitted yet, which for a user is indistinguishable
 *     from the moments right after pressing Invest.
 */
const STATUS_COPY: Record<
  string,
  { label: string; detail: string; tone: "neutral" | "good" | "attention" }
> = {
  created: {
    label: "Starting",
    detail: "We're setting up your transfer.",
    tone: "neutral",
  },
  pending: {
    label: "Transfer in progress",
    detail: "We've asked your bank to send the funds.",
    tone: "neutral",
  },
  submitted: {
    label: "Transfer sent",
    detail: "Your bank confirmed the transfer. This usually takes a few business days.",
    tone: "neutral",
  },
  funded: {
    label: "Funds received",
    detail: "Your investment is being held in escrow until the raise closes.",
    tone: "good",
  },
  closed: {
    label: "Invested",
    detail: "The raise closed and your investment is final.",
    tone: "good",
  },
  expiredsubmitted: {
    label: "Raise ended",
    detail: "The raise ended while your transfer was in flight. We'll be in touch.",
    tone: "attention",
  },
  refunding: {
    label: "Refund in progress",
    detail: "We're returning your funds.",
    tone: "attention",
  },
  canceled: {
    label: "Canceled",
    detail: "This investment didn't go through. No funds are being held.",
    tone: "neutral",
  },
  transactionerror: {
    label: "Needs attention",
    detail:
      "Something went wrong with this transfer. We're looking into it — contact us for details.",
    tone: "attention",
  },
  refunderror: {
    label: "Needs attention",
    detail: "We hit a problem returning your funds. Contact us and we'll sort it out.",
    tone: "attention",
  },
};

const TONE_CLASS: Record<"neutral" | "good" | "attention", string> = {
  neutral: "bg-withus-dark-blue/8 text-withus-dark-blue",
  good: "bg-withus-yellow text-withus-pure-black",
  attention: "bg-orange-100 text-orange-900",
};

// ---------------------------------------------------------------------------
// Portfolio — the tabbed card grid
// ---------------------------------------------------------------------------

/**
 * Everything the caller is following, as browse-style cards under four filter
 * tabs (Figma `1047:148`).
 *
 * The three lists are DISJOINT relationships, not slices of one collection —
 * nominating a business, reserving on it, and investing in it are different
 * acts, and the same person can have done any combination. So "All" is their
 * union, ordered investments → reservations → nominations: closest commitment
 * first, which is also the order in which a row is most likely to need
 * attention.
 */
function PortfolioSection({
  investments,
  pledgedCards,
  pledgedNominations,
  nominatedBusinesses,
}: {
  investments: readonly InvestorInvestmentRow[];
  pledgedCards: readonly InvestorPledgedCardRow[];
  pledgedNominations: readonly InvestorPledgedNominationRow[];
  nominatedBusinesses: readonly InvestorNominatedBusinessRow[];
}) {
  const [tab, setTab] = useState<PortfolioTab>("all");

  const showInvested = tab === "all" || tab === "invested";
  const showReserved = tab === "all" || tab === "reserved";
  const showNominated = tab === "all" || tab === "nominated";

  // A cancelled order still RENDERS — its status copy is how the user learns
  // what happened — so a portfolio holding only cancelled orders is not empty.
  // "Reserved" spans BOTH pledge stores, so every count and emptiness check
  // below sums them. Counting only `pledgedCards` is what made a dashboard full
  // of nomination pledges render its empty state.
  const reservedCount = pledgedCards.length + pledgedNominations.length;

  const isEmpty =
    (showInvested ? investments.length : 0) +
      (showReserved ? reservedCount : 0) +
      (showNominated ? nominatedBusinesses.length : 0) ===
    0;

  return (
    <section className="flex flex-col">
      {/* Mirrors `/browse`: the filter row sits in a `min-h-25` band so the
          grid below starts at the same y-position on both pages, whether or
          not the row wraps. */}
      <div className="flex min-h-25 flex-col justify-center gap-4">
        <PortfolioTabs
          active={tab}
          counts={{
            nominated: nominatedBusinesses.length,
            reserved: reservedCount,
            // Counts rows in the tab, not live money. A cancelled order still
            // RENDERS (its status copy is how the user learns what happened), so
            // excluding it here would put "Invested 0" above a visible card —
            // trading one contradiction for another. The card itself carries the
            // distinction: its stake bar reads "Canceled · $X", never "You
            // invested" (see `isCommittedOrderStatus`).
            invested: investments.length,
          }}
          onChange={setTab}
          className="-mx-4 px-4 md:mx-0 md:px-0"
        />
      </div>

      <div className="py-4">
        {isEmpty ? (
          <PortfolioEmptyState tab={tab} />
        ) : (
          // The same column ladder `/browse` uses — these are the same
          // `UnifiedBrowseCard`s, so they get the same breakpoints and the same
          // `gap-4` rather than a second, narrower set of rules.
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5">
            {showInvested &&
              investments.map((investment) => (
                <div key={investment.orderId} className="flex flex-col gap-2">
                  <InvestmentCard investment={investment} />
                  <InvestmentStatusLine investment={investment} />
                </div>
              ))}
            {showReserved &&
              pledgedCards.map((pledge) => (
                <ReservationCard key={pledge.cardId.toString()} pledge={pledge} />
              ))}
            {showReserved &&
              pledgedNominations.map((pledge) => (
                <NominationReservationCard key={pledge.nominatedBusinessId} pledge={pledge} />
              ))}
            {showNominated &&
              nominatedBusinesses.map((nomination) => (
                <NominationCard key={nomination.nominatedBusinessId} nomination={nomination} />
              ))}
          </div>
        )}
      </div>
    </section>
  );
}

/**
 * The order's lifecycle, in the user's language, under its card.
 *
 * The card itself shows the raise; this shows what is happening to THIS user's
 * money in it, which nothing else on the page says. It sits outside the card
 * rather than inside because it is about the order, not the business — and
 * because the card is shared with `/browse`, where no such thing exists.
 */
function InvestmentStatusLine({ investment }: { investment: InvestorInvestmentRow }) {
  // An unrecognized status must still render — the server can add a state
  // before this map does, and an investment silently losing its status would be
  // worse than a generic label.
  const copy = STATUS_COPY[statusKey(investment.status)] ?? {
    label: investment.status,
    detail: "Contact us for the latest on this investment.",
    tone: "neutral" as const,
  };

  return (
    <div className="flex flex-col gap-1">
      <span className={`w-fit px-2 py-1 text-xs font-medium ${TONE_CLASS[copy.tone]}`}>
        {copy.label}
      </span>
      <p className="text-xs text-muted-foreground">{copy.detail}</p>
    </div>
  );
}

/** Per-tab empty copy. A tab the user has simply never used should say so in
 *  its own terms rather than share one generic line. */
function PortfolioEmptyState({ tab }: { tab: PortfolioTab }) {
  const copy: Record<PortfolioTab, string> = {
    all: "Nothing here yet. Browse businesses to nominate, reserve, or invest.",
    nominated: "You haven't nominated a business yet.",
    reserved: "You haven't reserved an investment yet.",
    invested: "You haven't invested yet.",
  };

  return (
    <Card className="border-black/10">
      <CardContent className="flex flex-col items-start gap-3 p-6 text-sm text-muted-foreground">
        <p>{copy[tab]}</p>
        <Link to="/browse" className={buttonVariants({ variant: "outline" })}>
          Browse businesses
        </Link>
      </CardContent>
    </Card>
  );
}
